import React from "react";
import ReactTable from "react-table";
import Pagination from "../../../components/DatatablePagination";
import { AddCommaNumber } from "../../../helpers/Utils";
import { Row, CardTitle } from "reactstrap";
import { Colxx } from "../../../components/common/CustomBootstrap";
import IntlMessages from "../../../helpers/IntlMessages";

const BookingAnalytics = (props) => {
  const { data, loading } = props;

  if (loading) {
    return null;
  }

  const stats = data?.stats || {
    total: 0,
    by_status: {},
    by_source: {},
    total_guests: 0,
    avg_party_size: 0,
  };
  const tableData = data?.data || [];

  return (
    <div>
      <CardTitle>
        <i className="simple-icon-calendar mr-2" />
        <IntlMessages id="analytics.booking.title" />
      </CardTitle>

      {/* Summary Cards */}
      <Row className="mb-3">
        <Colxx xxs="6" sm="4" lg="2" className="mb-2">
          <div
            className="text-center p-3 text-white h-100"
            style={{ backgroundColor: "#007bff", borderRadius: "15px" }}
          >
            <h2 className="mb-0">{stats.total}</h2>
            <small>
              <IntlMessages id="analytics.booking.summary.total" />
            </small>
          </div>
        </Colxx>
        <Colxx xxs="6" sm="4" lg="2" className="mb-2">
          <div
            className="text-center p-3 text-white h-100"
            style={{ backgroundColor: "#ffc107", borderRadius: "15px" }}
          >
            <h2 className="mb-0">{stats.by_status?.pending || 0}</h2>
            <small>
              <IntlMessages id="analytics.booking.summary.pending" />
            </small>
          </div>
        </Colxx>
        <Colxx xxs="6" sm="4" lg="2" className="mb-2">
          <div
            className="text-center p-3 text-white h-100"
            style={{ backgroundColor: "#17a2b8", borderRadius: "15px" }}
          >
            <h2 className="mb-0">{stats.by_status?.confirmed || 0}</h2>
            <small>
              <IntlMessages id="analytics.booking.summary.confirmed" />
            </small>
          </div>
        </Colxx>
        <Colxx xxs="6" sm="4" lg="2" className="mb-2">
          <div
            className="text-center p-3 text-white h-100"
            style={{ backgroundColor: "#28a745", borderRadius: "15px" }}
          >
            <h2 className="mb-0">{stats.by_status?.completed || 0}</h2>
            <small>
              <IntlMessages id="analytics.booking.summary.completed" />
            </small>
          </div>
        </Colxx>
        <Colxx xxs="6" sm="4" lg="2" className="mb-2">
          <div
            className="text-center p-3 text-white h-100"
            style={{ backgroundColor: "#dc3545", borderRadius: "15px" }}
          >
            <h2 className="mb-0">{stats.by_status?.cancelled || 0}</h2>
            <small>
              <IntlMessages id="analytics.booking.summary.cancelled" />
            </small>
          </div>
        </Colxx>
        <Colxx xxs="6" sm="4" lg="2" className="mb-2">
          <div
            className="text-center p-3 text-white h-100"
            style={{ backgroundColor: "#6c757d", borderRadius: "15px" }}
          >
            <h2 className="mb-0">{stats.total_guests}</h2>
            <small>
              <IntlMessages id="analytics.booking.summary.guests" />
            </small>
          </div>
        </Colxx>
      </Row>

      {/* Source Statistics */}
      <Row className="mb-3">
        <Colxx xxs="12">
          <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
            {Object.entries(stats.by_source || {}).map(([source, count]) => {
              if (count === 0) return null;
              const sourceLabels = {
                phone: <IntlMessages id="analytics.booking.source.phone" />,
                walk_in: <IntlMessages id="analytics.booking.source.walk_in" />,
                website: <IntlMessages id="analytics.booking.source.website" />,
                app: <IntlMessages id="analytics.booking.source.app" />,
                facebook: (
                  <IntlMessages id="analytics.booking.source.facebook" />
                ),
                zalo: <IntlMessages id="analytics.booking.source.zalo" />,
              };
              return (
                <div
                  key={source}
                  className="badge badge-secondary p-2"
                  style={{ fontSize: "0.9rem" }}
                >
                  {sourceLabels[source] || source}: {count}
                </div>
              );
            })}
          </div>
        </Colxx>
      </Row>

      {/* Data Table */}
      <ReactTable
        defaultPageSize={10}
        data={tableData}
        columns={[
          {
            Header: <IntlMessages id="analytics.booking.table.stt" />,
            accessor: "stt",
            width: 50,
            Cell: (row) => <div className="text-center">{row.value}</div>,
          },
          {
            Header: <IntlMessages id="analytics.booking.table.date" />,
            accessor: "booking_date",
            width: 100,
            Cell: (row) => <div className="text-center">{row.value}</div>,
          },
          {
            Header: <IntlMessages id="analytics.booking.table.time" />,
            accessor: "booking_time",
            width: 70,
            Cell: (row) => (
              <div className="text-center">{row.value?.substring(0, 5)}</div>
            ),
          },
          {
            Header: <IntlMessages id="analytics.booking.table.customer" />,
            accessor: "customer_name",
            Cell: (row) => <div className="text-left">{row.value}</div>,
          },
          {
            Header: <IntlMessages id="analytics.booking.table.phone" />,
            accessor: "customer_phone",
            width: 110,
            Cell: (row) => <div className="text-center">{row.value}</div>,
          },
          {
            Header: <IntlMessages id="analytics.booking.table.party_size" />,
            accessor: "party_size",
            width: 80,
            Cell: (row) => (
              <div className="text-center font-weight-bold">{row.value}</div>
            ),
          },
          {
            Header: <IntlMessages id="analytics.booking.table.status" />,
            accessor: "status_display",
            width: 120,
            Cell: (row) => {
              const statusColors = {
                pending: "warning",
                confirmed: "info",
                completed: "success",
                cancelled: "danger",
                no_show: "secondary",
              };
              const status = row.original.status;
              return (
                <div className="text-center">
                  <span
                    className={`badge badge-${statusColors[status] || "secondary"}`}
                  >
                    {row.value}
                  </span>
                </div>
              );
            },
          },
          {
            Header: <IntlMessages id="analytics.booking.table.source" />,
            accessor: "source_display",
            width: 110,
            Cell: (row) => <div className="text-center">{row.value}</div>,
          },
        ]}
        minRows={0}
        showPageJump={false}
        showPageSizeOptions={false}
        PaginationComponent={Pagination}
        NoDataComponent={() => (
          <div className="text-center text-muted py-3">
            <IntlMessages id="analytics.booking.table.no_data" />
          </div>
        )}
        className="equal-columns-table"
      />
    </div>
  );
};

export default BookingAnalytics;
